import React from 'react';
import { CheckCircle, Users, Award, Wrench } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useSettings } from '../context/SettingsContext';

const AboutPage = () => {
    const { settings, loading } = useSettings();

    const aboutData = loading ? {
        title: 'Hakkımızda',
        description: '15 yılı aşkın deneyimimizle far ve stop tamir hizmetlerinde öncü firmayız.',
        content: '',
        aboutTitle: 'Ankara Merkez Far',
        aboutContent: '',
        aboutImage: 'https://images.pexels.com/photos/4489703/pexels-photo-4489703.jpeg?w=800&h=600&fit=crop'
    } : {
        title: settings.aboutTitle || 'Hakkımızda',
        description: settings.aboutDescription || '15 yılı aşkın deneyimimizle far ve stop tamir hizmetlerinde öncü firmayız.',
        content: settings.aboutContent || '',
        aboutTitle: settings.aboutTitle || 'Ankara Merkez Far',
        aboutContent: settings.aboutContent || 'İçi kararan, güneşten solan ve üzeri çizilmiş otomobil, kamyonet, otobüs ve motorsiklet FAR & STOP\'larınızı yeni gibi yapan uzman ekibiyiz.',
        aboutImage: settings.aboutImage || 'https://images.pexels.com/photos/4489703/pexels-photo-4489703.jpeg?w=800&h=600&fit=crop'
    };

    // Parse content by newlines for multiple paragraphs
    const contentParagraphs = aboutData.aboutContent ? aboutData.aboutContent.split('\n').filter(p => p.trim()) : [];

    return (
        <div className="min-h-screen">
            <SEO
                title={`${aboutData.title} | Ankara Merkez Far`}
                description={aboutData.description}
                keywords="hakkımızda ankara far, merkez far hakkında, ankara far tamircisi firma bilgi"
                url="https://ankaramerkezfar.com/hakkimizda"
            />
            <Header />

            {/* Page Header */}
            <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 md:py-20">
                <div className="container mx-auto px-4">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">{aboutData.title}</h1>
                    <p className="text-lg text-gray-300 max-w-2xl">
                        {aboutData.description}
                    </p>
                </div>
            </section>

            {/* About Content */}
            <section className="py-16 md:py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                {aboutData.aboutTitle}
                            </h2>
                            {contentParagraphs.length > 0 ? (
                                contentParagraphs.map((paragraph, index) => (
                                    <p key={index} className="text-gray-600 mb-4 leading-relaxed">
                                        {paragraph}
                                    </p>
                                ))
                            ) : (
                                <>
                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        İçi kararan, güneşten solan ve üzeri çizilmiş otomobil, kamyonet, otobüs ve motorsiklet FAR & STOP'larınızı yeni gibi yapan uzman ekibiyiz.
                                    </p>
                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        15 yılı aşkın sektör deneyimimiz ve uzman kadromuzla, her tür araç için far ve stop tamir, bakım ve yenileme hizmetleri sunuyoruz.
                                    </p>
                                    <p className="text-gray-600 leading-relaxed">
                                        Müşteri memnuniyeti bizim için en önemli ödüldür. Kaliteli işçilik, orijinal yedek parça kullanımı ve uygun fiyat politikamızla sektörde öncü konumdaki yerimizi koruyoruz.
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                            <img
                                src={aboutData.aboutImage}
                                alt={aboutData.aboutTitle}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
                        <div className="text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{settings.experienceYears || '15+'}</div>
                            <div className="text-gray-600">Yıllık Deneyim</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{settings.happyCustomers || '5000+'}</div>
                            <div className="text-gray-600">Mutlu Müşteri</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{settings.satisfaction || '100%'}</div>
                            <div className="text-gray-600">Müşteri Memnuniyeti</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{settings.servicesCount || '7'}</div>
                            <div className="text-gray-600">Uzman Hizmet</div>
                        </div>
                    </div>

                    {/* Why Choose Us */}
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                            Neden Bizi Seçmelisiniz?
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="text-blue-600" size={32} />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2">Kaliteli Hizmet</h3>
                                <p className="text-gray-600 text-sm">
                                    Profesyonel ekip ve modern ekipmanlarla en kaliteli hizmeti sunuyoruz.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Users className="text-blue-600" size={32} />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2">Uzman Kadro</h3>
                                <p className="text-gray-600 text-sm">
                                    Deneyimli ve sertifikalı uzman kadromuzla güvenilir hizmet.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Award className="text-blue-600" size={32} />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2">Garanti Belgeli</h3>
                                <p className="text-gray-600 text-sm">
                                    Tüm işlerimiz garanti belgeli ve sigortalıdır.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow">
                                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Wrench className="text-blue-600" size={32} />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2">Orijinal Parça</h3>
                                <p className="text-gray-600 text-sm">
                                    Sadece orijinal ve kaliteli yedek parça kullanıyoruz.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutPage;
